import { useEffect, useState } from 'react';
import {
  getConfirmingBanks,
  saveConfirmingBanks,
  type ConfirmingBank,
} from '../confirming/confirmingApi';

// Linha do editor: números como texto (limpar não pode zerar em silêncio).
interface BankRow {
  id: number;
  name: string;
  plafond: string;
  termDays: string;
  isTermFixed: boolean;
  contractReference: string;
}

const toRow = (bank: ConfirmingBank): BankRow => ({
  id: bank.id,
  name: bank.name,
  plafond: String(bank.plafond),
  termDays: String(bank.termDays),
  isTermFixed: bank.isTermFixed,
  contractReference: bank.contractReference ?? '',
});

const cellInput =
  'w-full px-2 py-1.5 border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20';

/**
 * Bancos com linha de Confirming: cada um tem o seu plafond, o seu prazo (fixo ou a acertar aviso a
 * aviso) e o nº do contrato que aparece no extrato. Grava a lista inteira.
 */
export default function ConfirmingBanksEditor() {
  const [rows, setRows] = useState<BankRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    getConfirmingBanks()
      .then((banks) => setRows(banks.map(toRow)))
      .catch(() => setError('Não foi possível carregar os bancos de Confirming.'))
      .finally(() => setIsLoading(false));
  }, []);

  const update = (index: number, patch: Partial<BankRow>) =>
    setRows((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const handleSave = async () => {
    setError(null);
    setSaved(false);
    const banks: ConfirmingBank[] = [];
    for (let index = 0; index < rows.length; index++) {
      const row = rows[index];
      if (!row.plafond.trim() || !row.termDays.trim()) {
        setError(`Indique o plafond e o prazo de ${row.name.trim() || 'cada banco'}.`);
        return;
      }
      const plafond = Number(row.plafond.trim());
      const termDays = Number(row.termDays.trim());
      if (!row.name.trim()) {
        setError('Cada banco tem de ter nome.');
        return;
      }
      if (!Number.isFinite(plafond) || plafond < 0 || !Number.isInteger(termDays) || termDays < 0) {
        setError(`O plafond e o prazo de ${row.name} têm de ser números maiores ou iguais a 0.`);
        return;
      }
      banks.push({
        id: row.id,
        name: row.name.trim(),
        plafond,
        termDays,
        isTermFixed: row.isTermFixed,
        contractReference: row.contractReference.trim() || null,
        sortOrder: index,
      });
    }

    setIsSaving(true);
    try {
      const updated = await saveConfirmingBanks(banks);
      setRows(updated.map(toRow));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'Não foi possível guardar os bancos.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="text-secondary-text text-sm">A carregar bancos…</div>;
  }

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto border border-border" style={{ borderRadius: 'var(--radius)' }}>
        <table className="w-full text-sm" style={{ minWidth: 640 }}>
          <thead>
            <tr className="bg-muted text-left">
              <th className="px-2 py-2">Banco</th>
              <th className="px-2 py-2" style={{ width: 130 }}>Plafond (€)</th>
              <th className="px-2 py-2" style={{ width: 90 }}>Prazo (dias)</th>
              <th className="px-2 py-2" style={{ width: 90 }}>Prazo fixo</th>
              <th className="px-2 py-2">Contrato no extrato</th>
              <th className="px-2 py-2" style={{ width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-2 py-3 text-secondary-text">
                  Sem bancos. Sem bancos, vale o plafond único e o prazo por omissão.
                </td>
              </tr>
            )}
            {rows.map((row, index) => (
              <tr key={`${row.id}-${index}`} className="border-t border-border">
                <td className="px-2 py-1.5">
                  <input
                    aria-label="Nome do banco"
                    className={cellInput}
                    style={{ borderRadius: 'var(--radius-button)' }}
                    value={row.name}
                    onChange={(e) => update(index, { name: e.target.value })}
                  />
                </td>
                <td className="px-2 py-1.5">
                  <input
                    aria-label={`Plafond de ${row.name || 'banco'}`}
                    type="number"
                    step="any"
                    className={cellInput}
                    style={{ borderRadius: 'var(--radius-button)' }}
                    value={row.plafond}
                    onChange={(e) => update(index, { plafond: e.target.value })}
                  />
                </td>
                <td className="px-2 py-1.5">
                  <input
                    aria-label={`Prazo de ${row.name || 'banco'}`}
                    type="number"
                    step={1}
                    className={cellInput}
                    style={{ borderRadius: 'var(--radius-button)' }}
                    value={row.termDays}
                    onChange={(e) => update(index, { termDays: e.target.value })}
                  />
                </td>
                <td className="px-2 py-1.5 text-center">
                  <input
                    aria-label={`Prazo fixo em ${row.name || 'banco'}`}
                    type="checkbox"
                    checked={row.isTermFixed}
                    onChange={(e) => update(index, { isTermFixed: e.target.checked })}
                  />
                </td>
                <td className="px-2 py-1.5">
                  <input
                    aria-label={`Contrato de ${row.name || 'banco'}`}
                    className={cellInput}
                    style={{ borderRadius: 'var(--radius-button)' }}
                    placeholder="ex.: 1120240000345"
                    value={row.contractReference}
                    onChange={(e) => update(index, { contractReference: e.target.value })}
                  />
                </td>
                <td className="px-2 py-1.5 text-center">
                  <button
                    type="button"
                    aria-label={`Remover ${row.name || 'banco'}`}
                    className="text-secondary-text hover:text-destructive"
                    onClick={() => setRows((current) => current.filter((_, i) => i !== index))}
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-secondary-text" style={{ fontSize: 'var(--text-caption)' }}>
        Prazo fixo: a data de liquidação de cada aviso calcula-se com o prazo. Sem prazo fixo, o prazo é só o ponto de
        partida e a data acerta-se aviso a aviso pela app do banco. O contrato é o nº do débito «DEBITO DE C.C. DE
        CONFIRMING - …»: liquida sozinho os avisos desse banco que vencem nesse dia; os débitos pequenos ficam como juros e
        custos.
      </p>
      {error && <div className="text-destructive text-sm">{error}</div>}
      {saved && (
        <div className="text-sm" style={{ color: 'var(--color-success, #16a34a)' }}>
          ✓ Bancos guardados.
        </div>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          className="px-3 py-2 border border-border bg-background text-foreground hover:bg-accent/30 text-sm"
          style={{ borderRadius: 'var(--radius-button)' }}
          onClick={() =>
            setRows((current) => [
              ...current,
              { id: 0, name: '', plafond: '0', termDays: '150', isTermFixed: false, contractReference: '' },
            ])
          }
        >
          + Adicionar banco
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground transition-colors disabled:opacity-60 text-sm"
          style={{ borderRadius: 'var(--radius-button)' }}
        >
          {isSaving ? 'A guardar...' : 'Guardar bancos'}
        </button>
      </div>
    </div>
  );
}
